import { Add, Delete } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IArnoTopObject } from '../Interfaces/IArnoTopObject';
import { IDocumentDirectoryTemplate } from '../Interfaces/IDocumentDirectoryTemplate';
import { IDocumentDirectoryTemplateHasDocument } from '../Interfaces/IDocumentDirectoryTemplateHasDocument';
import { ILegalPhaseTemplateFullObject } from '../Interfaces/ILegalPhaseTemplate';
import { ILegalRoleTemplate } from '../Interfaces/ILegalRoleTemplate';
import { ILegalRoleTemplate_canInvite } from '../Interfaces/ILegalRoleTemplate_canInvite';
import { type IMeasure } from '../Interfaces/ILegalTransactionMeasure';
import {
  ILegalTransactionType,
  ILegalTransactionTypeFullObject,
} from '../Interfaces/ILegalTransactionType';
import { deleteFetch, uploadFetch, useFetch } from '../hooks/useFetch';
import { LegalTransactionTypeCopy } from './LegalTransactionTypeCopy';
import {
  getEmptyLegalPhaseTemplateFullObject,
  getEmptyLegalTransactionTypeFullObject,
} from '../core/generic_helper';

import { MeasuresList } from './LegalTransactionMeasures/Measures/MeasuresList';

import { LegalPhaseTemplateTable } from './LegalPhaseTemplateTable';
import { MeetingEmailTemplateOverview } from './MeetingEmailTemplate/MeetingEmailTemplateOverview';
import { IMeetingEmailTemplate } from '../Interfaces/IMeetingEmailTemplate';
import CustomTabPanel from '../core/CustomTabPanel';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { DocumentDirectoryTemplateOverview } from './DocumentDirectoryTemplate/DocumentDirectoryTemplateOverview';
import { LegalRoleTemplateCanInviteTable } from './LegalRoleTemplate/LegalRoleTemplateCanInviteTable';
import { LegalRoleTemplateOverview } from './LegalRoleTemplate/LegalRoleTemplateOverview';
import { CustomeTextField } from '../generic/CustomeTextField';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface IProps {
  idLegalTransactionType: number;
  transactionTypeArray: ILegalTransactionType[];
  setTransactionTypeArray: (value: ILegalTransactionType[]) => void;
  setWasSaved: (value: boolean) => void;
  setWasSavedSuccessfully: (value: boolean) => void;
  handleAfterRemove: () => void;
  arnoTopObjects: IArnoTopObject[] | undefined;
}

export const LegalTransactionTypeEdit: React.FC<IProps> = ({
  idLegalTransactionType,
  transactionTypeArray,
  setTransactionTypeArray,
  setWasSaved,
  setWasSavedSuccessfully,
  handleAfterRemove,
  arnoTopObjects,
}) => {
  const theme = useTheme();
  const [selectedRequirementsIds, setSelectedRequirementsIds] = useState<
    number[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [
    legalTransactionTypeFullObject,
    setLegalTransactionTypeFullObject,
    wasSuccessfully,
  ] = useFetch<ILegalTransactionTypeFullObject>(
    '/legaltransactiontype/fullObject/',
    idLegalTransactionType
  );
  const [legalTransactionType, setLegalTransactionType] =
    useState<ILegalTransactionType>(getEmptyLegalTransactionTypeFullObject());
  const [legalPhaseTemplateArray, setLegalPhaseTemplateArray] = useState<
    ILegalPhaseTemplateFullObject[]
  >([]);
  const [legalRoleTemplateArray, setLegalRoleTemplateArray] = useState<
    ILegalRoleTemplate[]
  >([]);
  const [legalRoleTemplateCanInviteArray, setLegalRoleTemplateCanInviteArray] =
    useState<ILegalRoleTemplate_canInvite[]>([]);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [documentDirectoryTemplateArray, setDocumentDirectoryTemplateArray] =
    useState<IDocumentDirectoryTemplate[]>([]);
  const [documentArray, setDocumentArray] = useState<
    IDocumentDirectoryTemplateHasDocument[]
  >([]);
  const [meetingEmailTemplateArray, setMeetingEmailTemplateArray] = useState<
    IMeetingEmailTemplate[]
  >([]);
  const [currentTab, setCurrentTab] = useState(0);

  const legalRequirements = legalPhaseTemplateArray.flatMap(
    legalPhase => legalPhase.LegalRequirementArray
  );

  const [measures, setMeasures] = useState<IMeasure[]>([]);

  useEffect(() => {
    if (idLegalTransactionType === -1) {
      setLegalTransactionTypeFullObject(
        getEmptyLegalTransactionTypeFullObject()
      );
    }
  }, []);

  useEffect(() => {
    if (legalTransactionTypeFullObject !== undefined) {
      setLegalTransactionType(legalTransactionTypeFullObject);
      setLegalPhaseTemplateArray(
        legalTransactionTypeFullObject.LegalPhaseTemplateArray
      );
      setLegalRoleTemplateArray(
        legalTransactionTypeFullObject.LegalRoleTemplateArray
      );
      setLegalRoleTemplateCanInviteArray(
        legalTransactionTypeFullObject.LegalRoleTemplateCanInviteArray
      );
      setDocumentArray(legalTransactionTypeFullObject.DocumentArray);
      setDocumentDirectoryTemplateArray(
        legalTransactionTypeFullObject.DocumentDirectoryTemplateArray
      );
      setMeetingEmailTemplateArray(
        legalTransactionTypeFullObject.MeetingEmailTemplateArray
      );
      setMeasures(legalTransactionTypeFullObject.Measurements ?? []);
    }
  }, [legalTransactionTypeFullObject]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleAddNewLegalPhaseNew = () => {
    setLegalPhaseTemplateArray([
      ...legalPhaseTemplateArray,
      getEmptyLegalPhaseTemplateFullObject(legalPhaseTemplateArray),
    ]);
  };

  const wrapperSave = (
    localTransactionType: ILegalTransactionTypeFullObject
  ) => {
    if (legalTransactionType.idLegalTransactionType < 0) {
      setTransactionTypeArray([...transactionTypeArray, localTransactionType]);
    } else {
      setTransactionTypeArray([
        ...transactionTypeArray.map(transactionType =>
          transactionType.idLegalTransactionType ===
          localTransactionType.idLegalTransactionType
            ? localTransactionType
            : transactionType
        ),
      ]);
    }
    setLegalTransactionTypeFullObject(localTransactionType);
  };

  const deleteMultiFunction = () => {
    const newLegalPhaseArray = legalPhaseTemplateArray.map(legalPhase => ({
      ...legalPhase,
      LegalRequirementArray: legalPhase.LegalRequirementArray.filter(
        legalRequirement =>
          !selectedRequirementsIds.includes(
            legalRequirement.idLegalRequirementTemplate
          )
      ),
    }));

    setLegalPhaseTemplateArray(newLegalPhaseArray);
    setSelectedRequirementsIds([]);
  };

  const handleSave = async () => {
    const confirmed = await showConfirmDialogComponent(
      'Warnung',
      'Wenn Sie Ihre Änderungen speichern, bearbeiten Sie eine Vorlage. Ihre Änderungen werden künftig auf alle Vorgänge dieser Rechtsgeschäftsart angewendet. Bereits bestehende Vorgänge sind von den Änderungen nicht betroffen.',
      'Speichern',
      'Abbrechen',
      theme
    );

    if (!confirmed) {
      return;
    }

    if (legalTransactionTypeFullObject) {
      setIsLoading(true);
      const tmpObject = {
        ...legalTransactionType,
        withDialog: Boolean(legalTransactionType.withDialog),
        isTemplate: Boolean(legalTransactionType.isTemplate),
        LegalPhaseTemplateArray: legalPhaseTemplateArray,
        LegalRoleTemplateArray: legalRoleTemplateArray,
        LegalRoleTemplateCanInviteArray: legalRoleTemplateCanInviteArray,
        DocumentArray: documentArray,
        DocumentDirectoryTemplateArray: documentDirectoryTemplateArray,
        MeetingEmailTemplateArray: meetingEmailTemplateArray,
        Measurements: measures,
      } as ILegalTransactionTypeFullObject;
      uploadFetch(
        '/legaltransactiontype/fullObject',
        legalTransactionTypeFullObject.idLegalTransactionType < 0,
        tmpObject,
        wrapperSave,
        setWasSavedSuccessfully,
        setWasSaved,
        setIsLoading
      );
    }
  };

  const handleDelete = () => {
    if (idLegalTransactionType !== null) {
      deleteFetch(
        'legaltransactiontype',
        idLegalTransactionType,
        handleAfterRemove
      );
    }
  };

  if (!wasSuccessfully) {
    return <>Fehler!</>;
  } else if (legalTransactionTypeFullObject === undefined) {
    return <LoadingSpinner />;
  } else {
    return (
      <>
        <Dialog open={isOpenDelete} onClose={() => setIsOpenDelete(false)}>
          <DialogTitle>Löschen</DialogTitle>
          <DialogContent>
            Sind Sie sich sicher, dass Sie den aktuellen Vorgang löschen wollen?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsOpenDelete(false)}>Abbruch</Button>
            <Button onClick={handleDelete}>Löschen</Button>
          </DialogActions>
        </Dialog>

        <Collapse
          in={
            legalTransactionType.isTemplate &&
            legalTransactionType.idLegalTransactionType > 0
          }
        >
          <Alert severity='warning' sx={{ mb: 4 }}>
            <AlertTitle>Warnung</AlertTitle>
            Sie bearbeiten eine <strong>Vorlage</strong>!
          </Alert>
        </Collapse>

        <Collapse in={legalTransactionType.withDialog}>
          <Alert severity='warning' sx={{ mb: 4 }}>
            <AlertTitle>Warnung</AlertTitle>
            Diese Rechtsgeschäftsart hat einen öffentlichen Dialog und kann
            nicht gelöscht werden.
          </Alert>
        </Collapse>

        <Box
          sx={{
            position: 'sticky',
            top: 64,
            backgroundColor: 'white',
            zIndex: 1000,
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #ddd',
            padding: '16px 16px',
          }}
        >
          <Box p={2}>
            <Grid container spacing={2}>
              <Grid item sm={1}>
                <TextField
                  label='Vorlage'
                  value={legalTransactionType.isTemplate ? 1 : 0}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setLegalTransactionType({
                      ...legalTransactionType,
                      isTemplate: Number(event.target.value) === 1,
                    })
                  }
                  size='small'
                  fullWidth
                  select
                  disabled={legalTransactionType.idLegalTransactionType > 0}
                >
                  <MenuItem key={`isTemplate-1`} value={1}>
                    Ja
                  </MenuItem>
                  <MenuItem key={`isTemplate-0`} value={0}>
                    Nein
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid item sm={4}>
                <TextField
                  label='Art'
                  value={legalTransactionType.LegalTransactionType}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setLegalTransactionType({
                      ...legalTransactionType,
                      LegalTransactionType: event.target.value,
                    })
                  }
                  size='small'
                  fullWidth
                />
              </Grid>
              <Grid item sm={4}>
                {arnoTopObjects === undefined ? (
                  <Alert severity='warning' sx={{ mt: -0.5 }}>
                    ArnoTop Gegestände können nicht bezogen werden!
                  </Alert>
                ) : (
                  <CustomeTextField
                    attr='idArnoTopObject'
                    label='ArnoTop Gegenstand'
                    object={legalTransactionType}
                    setObject={setLegalTransactionType}
                    type='select'
                    null
                    required
                  >
                    {arnoTopObjects
                      .sort((a, b) =>
                        a.ArnoTopObject < b.ArnoTopObject ? -1 : 1
                      )
                      .map(arno => (
                        <MenuItem
                          key={`arnotop-object-${arno.idArnoTopObject}`}
                          value={arno.idArnoTopObject}
                        >
                          {arno.ArnoTopObject}
                        </MenuItem>
                      ))}
                  </CustomeTextField>
                )}
              </Grid>

              <Grid
                item
                sm={3}
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
              >
                {idLegalTransactionType > 0 &&
                  legalTransactionTypeFullObject !== undefined && (
                    <IconButton
                      disabled={legalTransactionTypeFullObject.withDialog}
                      onClick={() => setIsOpenDelete(true)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                <Button
                  variant='contained'
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  Speichern
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }} />
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={currentTab}
                onChange={handleChange}
                aria-label='basic tabs example'
              >
                <Tab label='Statusmeldung' {...a11yProps(0)} />
                <Tab label='Vorgangsmanager' {...a11yProps(1)} />
                <Tab label='Berechtigungen' {...a11yProps(2)} />
                <Tab label='Interne Datenablage' {...a11yProps(3)} />
                <Tab label='Termin-E-Mails' {...a11yProps(4)} />
              </Tabs>
            </Box>
            <Box sx={{ mt: 2 }} />
            {selectedRequirementsIds.length > 0 && currentTab === 0 && (
              <Button variant='outlined' onClick={deleteMultiFunction}>
                Löschen
              </Button>
            )}
          </Box>
          {isLoading && <LinearProgress sx={{ mt: 3, mb: 3 }} />}
        </Box>
        <Box p={2}>
          <Box
            ml={2}
            sx={{
              visibility:
                selectedRequirementsIds.length > 0 ? 'visible' : 'hidden',
            }}
          ></Box>
        </Box>

        <Box p={2}>
          <Box sx={{ mt: 2 }} />
          <CustomTabPanel value={currentTab} index={0}>
            <>
              <Typography variant='h6'>
                Vollzugsphasen
                <IconButton
                  sx={{ float: 'right' }}
                  onClick={handleAddNewLegalPhaseNew}
                >
                  <Add />
                </IconButton>
                <Box sx={{ float: 'right' }}>
                  <LegalTransactionTypeCopy
                    transactionTypeArray={transactionTypeArray}
                    legalPhaseTemplateArray={legalPhaseTemplateArray}
                    setLegalPhaseTemplateArray={setLegalPhaseTemplateArray}
                  />
                </Box>
              </Typography>
              <LegalPhaseTemplateTable
                legalPhaseTemplateArray={legalPhaseTemplateArray}
                setLegalPhaseTemplateArray={setLegalPhaseTemplateArray}
                selectedRequirementsIds={selectedRequirementsIds}
                setSelectedRequirementsIds={setSelectedRequirementsIds}
              />
            </>
          </CustomTabPanel>
          <CustomTabPanel value={currentTab} index={1}>
            <MeasuresList
              legalRequirements={[]}
              legalRequirementTemplates={legalRequirements}
              measures={measures}
              setMeasures={setMeasures}
            />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={2}>
            <LegalRoleTemplateOverview
              legalRoleTemplateArray={legalRoleTemplateArray}
              setLegalRoleTemplateArray={setLegalRoleTemplateArray}
            />

            <LegalRoleTemplateCanInviteTable
              legalRoleTemplateArray={legalRoleTemplateArray}
              legalRoleTemplateCanInviteArray={legalRoleTemplateCanInviteArray}
              setLegalRoleTemplateCanInviteArray={
                setLegalRoleTemplateCanInviteArray
              }
            />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={3}>
            <DocumentDirectoryTemplateOverview
              documentArray={documentArray}
              documentDirectoryArray={documentDirectoryTemplateArray}
              setDocumentArray={setDocumentArray}
              setDocumentDirectoryArray={setDocumentDirectoryTemplateArray}
            />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={4}>
            <MeetingEmailTemplateOverview
              idLegalTransactionType={
                legalTransactionType.idLegalTransactionType
              }
              meetingEmailTemplateArray={meetingEmailTemplateArray}
              setMeetingEmailTemplateArray={setMeetingEmailTemplateArray}
            />
          </CustomTabPanel>
        </Box>
      </>
    );
  }
};
